import axios from 'axios';
import { API_CONFIG, LOG_CONFIG } from '../config/api';

// 使用统一的API配置
const TASK_MANAGER_BASE = API_CONFIG.TASK_MANAGER_BASE;

export interface StatisticsOverview {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  tasks: {
    total: number;
    successful: number;
    failed: number;
    success_rate: number;
    avg_execution_time: number;
  };
  activities: {
    total: number;
    unique_visitors: number;
    unique_users: number;
  };
  services: {
    total_records: number;
    total_requests: number;
    successful_requests: number;
    avg_execution_time: number;
  };
}

export interface TaskStatistics {
  daily_stats: Array<{
    date: string;
    total: number;
    successful: number;
    failed: number;
    avg_time: number;
  }>;
  service_stats: Array<{
    service_name: string;
    task_type: string;
    handler_name: string;
    total: number;
    successful: number;
    failed: number;
    avg_time: number;
  }>;
}

export interface UserActivities {
  daily_activities: Array<{
    date: string;
    total: number;
    unique_visitors: number;
  }>;
  activity_types: Array<{
    activity_type: string;
    total: number;
    unique_visitors: number;
  }>;
}

export interface ServiceUsage {
  daily_usage: Array<{
    date: string;
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    avg_time: number;
  }>;
  service_usage: Array<{
    service_name: string;
    task_type: string;
    total_requests: number;
    successful_requests: number;
    avg_time: number;
  }>;
}

class TaskStatisticsClient {
  private baseURL: string;

  constructor(baseURL: string = TASK_MANAGER_BASE) {
    this.baseURL = baseURL;
  }

  /**
   * 获取统计概览
   */
  async getOverview(days: number = 7): Promise<StatisticsOverview> {
    try {
      const response = await axios.get(`${this.baseURL}/statistics/overview`, {
        params: { days },
        timeout: API_CONFIG.TIMEOUT.DEFAULT,
        withCredentials: true  // 添加认证配置
      });
      return response.data;
    } catch (error) {
      console.error('获取统计概览失败:', error);
      throw error;
    }
  }

  /**
   * 获取任务统计
   */
  async getTaskStatistics(
    days: number = 30,
    serviceName?: string,
    taskType?: string,
    userId?: string
  ): Promise<TaskStatistics> {
    try {
      const response = await axios.get(`${this.baseURL}/statistics/tasks`, {
        params: { days, service_name: serviceName, task_type: taskType, user_id: userId },
        timeout: API_CONFIG.TIMEOUT.DEFAULT,
        withCredentials: true  // 添加认证配置
      });
      return response.data;
    } catch (error) {
      console.error('获取任务统计失败:', error);
      throw error;
    }
  }

  /**
   * 获取用户活动统计
   */
  async getUserActivities(
    days: number = 7,
    activityType?: string
  ): Promise<UserActivities> {
    try {
      const response = await axios.get(`${this.baseURL}/statistics/activities`, {
        params: { days, activity_type: activityType },
        timeout: API_CONFIG.TIMEOUT.DEFAULT,
        withCredentials: true  // 添加认证配置
      });
      return response.data;
    } catch (error) {
      console.error('获取用户活动统计失败:', error);
      throw error;
    }
  }

  /**
   * 获取服务使用统计
   */
  async getServiceUsage(
    days: number = 7,
    serviceName?: string
  ): Promise<ServiceUsage> {
    try {
      const response = await axios.get(`${this.baseURL}/statistics/services`, {
        params: { days, service_name: serviceName },
        timeout: API_CONFIG.TIMEOUT.DEFAULT
      });
      return response.data;
    } catch (error) {
      console.error('获取服务使用统计失败:', error);
      throw error;
    }
  }

  /**
   * 获取访客列表
   */
  async getVisitorsList(
    skip: number = 0,
    limit: number = 100,
    search?: string
  ): Promise<{
    visitors: Array<{
      visitor_id: string;
      ip_address: string;
      user_agent: string;
      first_visit: string;
      last_visit: string;
      visit_count: number;
      data_submissions: number;
    }>;
    total: number;
    skip: number;
    limit: number;
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/statistics/visitors`, {
        params: { skip, limit, search },
        timeout: API_CONFIG.TIMEOUT.DEFAULT,
        withCredentials: true  // 添加认证配置
      });
      return response.data;
    } catch (error) {
      console.error('获取访客列表失败:', error);
      throw error;
    }
  }

  /**
   * 记录用户活动（通过任务管理系统）
   */
  async recordActivity(
    activityType: string,
    visitorId: string,
    data: {
      user_id?: string;
      page_url?: string;
      service_name?: string;
      task_type?: string;
      ip_address?: string;
      user_agent?: string;
      activity_data?: any;
      duration?: number;
    }
  ): Promise<void> {
    try {
      // 这里可以通过任务管理系统的API记录活动
      // 或者直接调用统计API
      console.log('记录用户活动:', { activityType, visitorId, ...data });
    } catch (error) {
      console.error('记录用户活动失败:', error);
    }
  }
}

// 创建全局实例
export const taskStatisticsClient = new TaskStatisticsClient();

// 便捷函数
export const getStatisticsOverview = (days?: number) => 
  taskStatisticsClient.getOverview(days);

export const getTaskStatistics = (days?: number, serviceName?: string, taskType?: string, userId?: string) =>
  taskStatisticsClient.getTaskStatistics(days, serviceName, taskType, userId);

export const getUserActivities = (days?: number, activityType?: string) =>
  taskStatisticsClient.getUserActivities(days, activityType);

export const getServiceUsage = (days?: number, serviceName?: string) =>
  taskStatisticsClient.getServiceUsage(days, serviceName);

export const getVisitorsList = (skip?: number, limit?: number, search?: string) =>
  taskStatisticsClient.getVisitorsList(skip, limit, search);

export const recordActivity = (activityType: string, visitorId: string, data?: any) =>
  taskStatisticsClient.recordActivity(activityType, visitorId, data || {});